import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { createMarketplaceCrossLaunch, createOpsCrossLaunch } from "../../api/auth";
import { BRAND_ICON } from "../../config/brand";
import { API_BASE, buildAuthHeaders } from "../../api/base";
import { buildHostUrl } from "../../config/hostRouting";
import { useAuth } from "../../context/AuthContext";
import { useBrand } from "../../context/BrandContext";
import { useRentalsFeature } from "../../context/RentalsFeatureContext";
import { resolveAssetUrl } from "../../utils/assetUrl";
import { getEllyannaAuthenticatedLandingPath } from "../../utils/ellyannaShellRouting";
import {
  canOpenMyBoothShare,
  canAccessMarketplaceOperatorShell,
  canAccessMarketplaceRenterHistory,
} from "../../utils/marketplaceOperatorAccess";
import {
  getMarketplaceAccessNeededPath,
  getMarketplaceCrossLaunchPath,
  getMarketplacePostSalonSwitchPath,
} from "../../utils/marketplaceShellRouting";
import {
  getMarketplaceOperatorHubPath,
  getMarketplaceOperatorPaymentsPath,
  getMarketplaceOperatorSettingsPath,
  getMarketplaceOperatorWorkspacesPath,
} from "../../utils/marketplaceOperatorRoutes";

const SALON_SWITCH_OPTION_STYLE = {
  color: "#0f172a",
  backgroundColor: "#ffffff",
  fontWeight: 600,
};

const NavIcon = ({ children, className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`h-4 w-4 shrink-0 ${className}`}
    aria-hidden="true"
  >
    {children}
  </svg>
);

const SearchNavIcon = () => (
  <NavIcon>
    <circle cx="11" cy="11" r="6.5" />
    <path d="M16 16l4 4" />
  </NavIcon>
);

const HostNavIcon = () => (
  <NavIcon>
    <path d="M4 11.5L12 5l8 6.5" />
    <path d="M6 10.5V20h12v-9.5" />
    <path d="M9.5 20v-5h5v5" />
  </NavIcon>
);

const OperationsNavIcon = () => (
  <NavIcon>
    <rect x="3" y="4" width="7" height="7" rx="1.5" />
    <rect x="14" y="4" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </NavIcon>
);

const BookingsNavIcon = () => (
  <NavIcon>
    <rect x="3" y="5" width="18" height="16" rx="3" />
    <path d="M8 3v4M16 3v4M3 10h18" />
  </NavIcon>
);

const ProfileNavIcon = () => (
  <NavIcon>
    <circle cx="12" cy="8" r="3.2" />
    <path d="M5 19c1.7-3 4.1-4.5 7-4.5S17.3 16 19 19" />
  </NavIcon>
);

const SuperAdminNavIcon = () => (
  <NavIcon>
    <path d="M12 3l7 3v5c0 4.5-2.7 7.5-7 10-4.3-2.5-7-5.5-7-10V6l7-3z" />
    <path d="M12 8v5" />
    <path d="M9.5 10.5h5" />
  </NavIcon>
);

const MobileMenuIcon = ({ open }) => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    className="h-5 w-5"
    aria-hidden="true"
  >
    {open ? (
      <path
        d="M5 5l10 10M15 5L5 15"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : (
      <path
        d="M3 5h14M3 10h14M3 15h14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    )}
  </svg>
);

const MarketplaceNavLabel = ({ icon: Icon, label }) => (
  <span className="group relative inline-flex items-center justify-center gap-2">
    <Icon />
    <span aria-hidden="true" className="hidden lg:inline">
      {label}
    </span>
    <span className="sr-only lg:hidden">{label}</span>
    <span
      aria-hidden="true"
      className="pointer-events-none absolute left-1/2 top-full z-40 mt-2 hidden -translate-x-1/2 whitespace-nowrap rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 md:block lg:hidden"
    >
      {label}
    </span>
  </span>
);

const publicMarketplaceLandingLinks = [
  { href: "/#for-salons", label: "For Salons" },
  { href: "/#for-independent-professionals", label: "For Pros" },
  { href: "/#how-it-works", label: "How It Works" },
  { href: "/#benefits", label: "Benefits" },
];

const publicAccountingLandingLinks = [
  { href: "/#services", label: "Services" },
  { href: "/#connected", label: "Connected" },
  { href: "/#who-it-helps", label: "Who it helps" },
  { href: "/#why-different", label: "Why different" },
];

const formatNavSalonLabel = (salon) => {
  if (!salon) {
    return "";
  }
  const id = salon.id ?? salon.salon_id ?? salon.salonId;
  const name = salon.name ?? salon.salon_name ?? salon.salonName ?? "";
  if (id != null && name) {
    return `Salon ID ${id} - ${name}`;
  }
  if (id != null) {
    return `Salon ID ${id}`;
  }
  return name;
};

const getIndependentProfessionalNumber = (user) => {
  const directValue =
    user?.independent_operator_number ??
    user?.independentOperatorNumber ??
    user?.renter_profile?.independent_operator_number ??
    user?.renterProfile?.independentOperatorNumber;
  const parsedDirect = Number(directValue);
  if (Number.isFinite(parsedDirect) && parsedDirect > 0) {
    return parsedDirect;
  }

  const rawId =
    user?.independent_operator_id ??
    user?.independentOperatorId ??
    user?.renter_profile?.independent_operator_id ??
    user?.renterProfile?.independentOperatorId ??
    "";
  const match = String(rawId).match(/(\d+)$/);
  const parsedFromId = match ? Number(match[1]) : null;
  return Number.isFinite(parsedFromId) && parsedFromId > 0 ? parsedFromId : null;
};

const formatIndependentProfessionalContextLabel = (user, salon) => {
  const professionalNumber = getIndependentProfessionalNumber(user);
  const name =
    salon?.name ??
    salon?.salon_name ??
    salon?.salonName ??
    user?.business_name ??
    user?.full_name ??
    user?.name ??
    "";
  if (professionalNumber) {
    return name
      ? `Professional ID ${professionalNumber} - ${name}`
      : `Professional ID ${professionalNumber}`;
  }
  return name || "";
};

export default function MarketplaceHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    token,
    logout,
    user,
    currentSalon,
    accessibleSalons,
    selectSalon,
  } = useAuth();
  const { enabled: rentalsEnabled } = useRentalsFeature();
  const brand = useBrand();
  const [isSwitchingSalon, setIsSwitchingSalon] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [salonSwitchError, setSalonSwitchError] = useState("");
  const [staffPhotoUrl, setStaffPhotoUrl] = useState("");
  const [failedAvatarUrl, setFailedAvatarUrl] = useState("");
  const showOperatorLink =
    Boolean(token) &&
    canAccessMarketplaceOperatorShell({ role: user?.role, user }) &&
    (rentalsEnabled || Boolean(user?.salon_id));
  const showSuperAdminLink = Boolean(token) && Boolean(user?.is_super_admin);
  const showRenterNav =
    Boolean(token) && !showOperatorLink && canAccessMarketplaceRenterHistory(user);
  const currentSalonId = Number(
    currentSalon?.salon_id ?? currentSalon?.id ?? user?.salon_id ?? null
  );
  const currentSalonContext = {
    salon_id: currentSalon?.salon_id ?? currentSalon?.id ?? user?.salon_id ?? null,
    salon_name:
      currentSalon?.salon_name ??
      currentSalon?.name ??
      user?.salon_name ??
      "",
  };
  const isIndependentProfessional =
    String(user?.role || "").toLowerCase() === "independent_professional";
  const currentSalonLabel =
    (isIndependentProfessional
      ? formatIndependentProfessionalContextLabel(user, currentSalonContext)
      : formatNavSalonLabel(currentSalonContext)) || "No salon selected";
  const userDisplayName = (
    user?.full_name || user?.name || user?.email || ""
  ).trim();
  const resolvedUserPhotoUrl = resolveAssetUrl(user?.photo_url || staffPhotoUrl || "");
  const userAvatarUrl =
    resolvedUserPhotoUrl && resolvedUserPhotoUrl !== failedAvatarUrl
      ? resolvedUserPhotoUrl
      : "";
  const userInitials = useMemo(() => {
    const source = (userDisplayName || user?.email || "").trim();
    if (!source) {
      return "MB";
    }
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  }, [userDisplayName, user?.email]);
  const salonContextLabelWithUser = userDisplayName
    ? `${userDisplayName} • ${currentSalonLabel}`
    : currentSalonLabel;
  const accessibleSalonList = Array.isArray(accessibleSalons)
    ? accessibleSalons.filter((entry) => {
        const entryId = Number(entry?.salon_id ?? entry?.id ?? entry?.salonId ?? null);
        return Number.isFinite(entryId) && entryId > 0;
      })
    : [];
  const hasDirectOpsAccess =
    Boolean(token) &&
    (Boolean(user?.is_super_admin) ||
      Boolean(currentSalonId) ||
      accessibleSalonList.length > 0);
  const opsLaunchPath = useMemo(
    () => getEllyannaAuthenticatedLandingPath({ role: user?.role, user }),
    [user]
  );
  const opsTargetHost = useMemo(() => {
    const configuredHost = String(brand?.opsHost || "").trim();
    if (configuredHost) {
      return configuredHost;
    }
    try {
      return new URL(brand?.opsHomeUrl || "").host || "";
    } catch {
      return "";
    }
  }, [brand?.opsHomeUrl, brand?.opsHost]);
  const opsLaunchHref = useMemo(() => {
    if (!token) {
      return brand.opsLoginUrl;
    }
    if (!opsTargetHost) {
      return brand.opsHomeUrl;
    }
    return buildHostUrl(opsTargetHost, opsLaunchPath);
  }, [brand.opsHomeUrl, brand.opsLoginUrl, opsLaunchPath, opsTargetHost, token]);
  const hasSalonSwitcher = Boolean(token) && accessibleSalonList.length > 1;
  const showSalonContext = Boolean(token && currentSalonId);
  const isAccountingHost = Boolean(brand?.isAccountingHost);
  const productLabel = isAccountingHost ? "SalonBusinessCPA" : "MyBoothShare";
  const roleKey = String(user?.role || "").toLowerCase();
  const canLaunchMyBoothShare = canOpenMyBoothShare({ role: roleKey, user });
  const marketplaceSwitchHref = buildHostUrl(
    brand?.marketplaceHostName || "myboothshare.com",
    canLaunchMyBoothShare
      ? getMarketplaceCrossLaunchPath({
          isAuthenticated: Boolean(token),
          role: roleKey,
          user,
        })
      : getMarketplaceAccessNeededPath({
          role: roleKey,
          source: "product_account_menu",
        })
  );
  const accountingSwitchHref = buildHostUrl(
    brand?.accountingHostName || "salonbusinesscpa.com",
    "/login",
    "?next=%2Fsalonbusinesscpa"
  );

  useEffect(() => {
    if (!token || !currentSalonId) {
      setStaffPhotoUrl("");
      return;
    }
    let cancelled = false;
    const loadStaffPhoto = async () => {
      try {
        const response = await fetch(`${API_BASE}/users/me/profile`, {
          headers: buildAuthHeaders(token),
          credentials: "include",
        });
        if (!response.ok) {
          if (!cancelled) setStaffPhotoUrl("");
          return;
        }
        const staffProfile = await response.json();
        if (cancelled) return;
        const nextPhotoUrl = staffProfile?.photo_url || "";
        setStaffPhotoUrl(resolveAssetUrl(nextPhotoUrl));
      } catch {
        if (!cancelled) setStaffPhotoUrl("");
      }
    };
    loadStaffPhoto();
    return () => {
      cancelled = true;
    };
  }, [currentSalonId, token, user?.id]);

  useEffect(() => {
    setFailedAvatarUrl("");
  }, [resolvedUserPhotoUrl]);

  const handleAvatarError = (event) => {
    const failedUrl = event?.currentTarget?.currentSrc || event?.currentTarget?.src || "";
    setFailedAvatarUrl(failedUrl || resolvedUserPhotoUrl);
  };

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsAccountMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleLogout = async () => {
    setIsMobileMenuOpen(false);
    await logout();
    navigate(isAccountingHost ? "/" : "/search", { replace: true });
  };

  const handleOpsLaunch = useCallback(
    async (event) => {
      if (!token) {
        setIsMobileMenuOpen(false);
        setIsAccountMenuOpen(false);
        return;
      }
      event?.preventDefault();
      event?.stopPropagation();
      setIsMobileMenuOpen(false);
      setIsAccountMenuOpen(false);
      try {
        const launch = await createOpsCrossLaunch(opsLaunchPath);
        const nextPath = String(launch?.next_path || "").trim() || opsLaunchPath;
        const targetUrl = opsTargetHost
          ? new URL(buildHostUrl(opsTargetHost, nextPath))
          : new URL(opsLaunchHref);
        if (launch?.launch_token) {
          targetUrl.searchParams.set("launch_token", launch.launch_token);
        }
        window.location.assign(targetUrl.toString());
      } catch (err) {
        console.warn("Unable to create Ellyanna cross launch", err);
        window.location.assign(opsLaunchHref);
      }
    },
    [opsLaunchHref, opsLaunchPath, opsTargetHost, token]
  );

  const handleMarketplaceLaunch = useCallback(
    async (event) => {
      if (!token || !canLaunchMyBoothShare) {
        setIsMobileMenuOpen(false);
        setIsAccountMenuOpen(false);
        return;
      }
      event?.preventDefault();
      event?.stopPropagation();
      setIsMobileMenuOpen(false);
      setIsAccountMenuOpen(false);
      try {
        const launch = await createMarketplaceCrossLaunch();
        const launchPath =
          String(launch?.next_path || "").trim() ||
          getMarketplaceCrossLaunchPath({
            isAuthenticated: true,
            role: roleKey,
            user,
          });
        const url = new URL(
          buildHostUrl(brand?.marketplaceHostName || "myboothshare.com", launchPath)
        );
        if (launch?.launch_token) {
          url.searchParams.set("launch_token", launch.launch_token);
        }
        window.location.assign(url.toString());
      } catch (err) {
        console.warn("Unable to create marketplace cross launch", err);
        window.location.assign(marketplaceSwitchHref);
      }
    },
    [brand?.marketplaceHostName, canLaunchMyBoothShare, marketplaceSwitchHref, roleKey, token, user]
  );

  const handleSimpleProductLaunch = useCallback(() => {
    setIsMobileMenuOpen(false);
    setIsAccountMenuOpen(false);
  }, []);

  const handleSalonChange = async (event) => {
    if (!selectSalon) {
      return;
    }
    const nextSalonId = Number(event.target.value || null);
    if (!Number.isFinite(nextSalonId) || nextSalonId <= 0 || nextSalonId === currentSalonId) {
      return;
    }

    setSalonSwitchError("");
    setIsSwitchingSalon(true);
    try {
      const switched = await selectSalon(nextSalonId);
      const nextPath = getMarketplacePostSalonSwitchPath({
        pathname: location.pathname,
        search: location.search,
        role: switched?.role,
        user: switched,
      });
      const currentPath = `${location.pathname}${location.search || ""}`;
      if (nextPath !== currentPath) {
        navigate(nextPath, { replace: true });
      }
    } catch (err) {
      setSalonSwitchError(err?.message || "Unable to switch salons right now.");
    } finally {
      setIsSwitchingSalon(false);
    }
  };

  const linkClass = ({ isActive }) =>
    `inline-flex h-8 shrink-0 items-center rounded-md px-1.5 text-sm font-medium transition lg:h-9 lg:px-3 ${
      isActive
        ? "bg-slate-800 text-white"
        : "text-slate-200 hover:bg-slate-800 hover:text-white"
    }`;
  const mobileLinkClass =
    "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-100 transition hover:bg-slate-800";
  const actionButtonClass = isAccountingHost
    ? "inline-flex h-10 shrink-0 items-center rounded-md border border-slate-500/70 px-4 text-sm font-semibold text-slate-100 transition hover:bg-slate-800"
    : "inline-flex h-8 shrink-0 items-center rounded-md border border-slate-500/70 px-2 text-xs font-medium text-slate-100 transition hover:bg-slate-800 lg:h-9 lg:px-3 lg:text-sm";
  const externalShellButtonClass = isAccountingHost
    ? "inline-flex h-10 shrink-0 items-center justify-center rounded-md border border-sky-300/45 bg-[#3f4e8b] px-4 text-sm font-semibold text-slate-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition hover:bg-[#47599d] hover:border-sky-200/60"
    : "inline-flex h-9 w-[108px] shrink-0 items-center justify-center overflow-hidden rounded-xl border border-sky-300/45 bg-[#3f4e8b] px-2 text-center text-[8px] font-semibold leading-tight text-slate-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition hover:bg-[#47599d] hover:border-sky-200/60 whitespace-normal break-words lg:h-11 lg:w-auto lg:px-5 lg:text-base lg:leading-none lg:whitespace-nowrap";
  const primaryButtonClass = isAccountingHost
    ? "inline-flex h-10 shrink-0 items-center rounded-md bg-rose-500 px-4 text-sm font-semibold text-white transition hover:bg-rose-600"
    : "inline-flex h-8 shrink-0 items-center rounded-md bg-rose-500 px-2 text-xs font-medium text-white transition hover:bg-rose-600 lg:h-9 lg:px-3 lg:text-sm";
  const accountMenuItemClass =
    "block w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-100";
  const accountMenuServiceLinkClass =
    "mt-1 flex w-full items-center justify-center rounded-lg border border-sky-300 bg-sky-50 px-3 py-2 text-center text-sm font-semibold text-sky-800 transition hover:bg-sky-100";
  const mobileAccountMenuServiceLinkClass =
    "flex w-full items-center justify-center rounded-lg border border-sky-400/40 bg-sky-500/10 px-3 py-2.5 text-center text-sm font-semibold text-sky-100 transition hover:bg-sky-500/20";
  const registerHref = buildHostUrl(brand?.opsHost || "ellyanna.com", "/register");
  const profilePath = isAccountingHost ? "/salonbusinesscpa" : "/account/profile";
  const navItems = isAccountingHost
    ? token
      ? [
          { to: "/salonbusinesscpa", label: "Dashboard", icon: OperationsNavIcon, end: true },
          { to: "/salonbusinesscpa/profile", label: "Setup", icon: ProfileNavIcon },
          { to: "/salonbusinesscpa/requests", label: "Requests", icon: BookingsNavIcon },
          { to: "/salonbusinesscpa/documents", label: "Documents", icon: BookingsNavIcon },
          { to: "/salonbusinesscpa/checklist", label: "Checklists", icon: ProfileNavIcon },
          ...(showSuperAdminLink
            ? [{ to: "/salonbusinesscpa/review", label: "Review", icon: SuperAdminNavIcon }]
            : []),
        ]
      : []
    : token
    ? showOperatorLink
      ? [
          { to: getMarketplaceOperatorHubPath(), label: "Dashboard", icon: OperationsNavIcon, end: true },
          { to: getMarketplaceOperatorWorkspacesPath(), label: "Workspaces", icon: HostNavIcon },
          { to: getMarketplaceOperatorPaymentsPath(), label: "Payments", icon: BookingsNavIcon },
          { to: getMarketplaceOperatorSettingsPath(), label: "Settings", icon: ProfileNavIcon },
        ]
      : showRenterNav
      ? [
          { to: "/search", label: "Search Workspaces", icon: SearchNavIcon, end: true },
          { to: "/account/rentals", label: "My Rentals", icon: HostNavIcon },
          { to: "/account/payments", label: "Payments", icon: BookingsNavIcon },
          { to: "/account/profile", label: "Profile", icon: ProfileNavIcon },
        ]
      : [{ to: "/search", label: "Search Workspaces", icon: SearchNavIcon, end: true }]
    : [{ to: "/search", label: "Search Workspaces", icon: SearchNavIcon, end: true }];
  const showPublicMarketplaceLandingLinks = !token && !isAccountingHost;
  const showPublicAccountingLandingLinks = !token && isAccountingHost;

  return (
    <header className="sticky top-0 z-[2600] border-b border-slate-700/70 bg-[#2f3566] text-white shadow-sm">
      <div className="mx-auto max-w-[1400px] overflow-visible px-3 py-3 lg:px-4">
        <div className="flex min-w-0 items-center justify-between gap-1.5 lg:gap-3">
          <Link
            to="/"
            className="inline-flex min-w-0 shrink items-center gap-2 lg:gap-3"
            aria-label={brand.appLabel}
          >
            <img
              src={BRAND_ICON}
              alt="Ellyanna"
              className="h-8 w-8 flex-shrink-0 lg:h-10 lg:w-10"
            />
            <div className="min-w-0 leading-none">
              <div className="text-base font-semibold text-white lg:text-xl">Ellyanna</div>
              <div className="mt-1 hidden text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-300 lg:block lg:text-[10px] lg:tracking-[0.22em]">
                {productLabel}
              </div>
            </div>
          </Link>
          <nav className="hidden min-w-0 flex-1 items-center md:flex">
            <div className="flex min-w-0 flex-nowrap items-center gap-0.5 pl-3 lg:gap-1.5 lg:pl-6">
              {navItems.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
                  <MarketplaceNavLabel icon={item.icon} label={item.label} />
                </NavLink>
              ))}
              {showPublicMarketplaceLandingLinks
                ? publicMarketplaceLandingLinks.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      className="inline-flex h-8 shrink-0 items-center rounded-md px-1.5 text-sm font-medium text-slate-200 transition hover:bg-slate-800 hover:text-white lg:h-9 lg:px-2"
                    >
                      {item.label}
                    </a>
                  ))
                : null}
              {showPublicAccountingLandingLinks
                ? publicAccountingLandingLinks.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      className="inline-flex h-8 shrink-0 items-center rounded-md px-1.5 text-sm font-medium text-slate-200 transition hover:bg-slate-800 hover:text-white lg:h-9 lg:px-2"
                    >
                      {item.label}
                    </a>
                  ))
                : null}
              {showSuperAdminLink && !isAccountingHost ? (
                <NavLink to="/superadmin" className={linkClass}>
                  <MarketplaceNavLabel
                    icon={SuperAdminNavIcon}
                    label="Super Admin"
                  />
                </NavLink>
              ) : null}
            </div>
            <div className="ml-auto flex items-center gap-0.5 lg:gap-1.5">
              {token && !hasDirectOpsAccess ? (
                <Link
                  to="/account/ellyanna-access"
                  className={actionButtonClass}
                >
                  Get Ellyanna Access
                </Link>
              ) : (
                <a
                  href={opsLaunchHref}
                  onClick={handleOpsLaunch}
                  className={externalShellButtonClass}
                >
                  Open Ellyanna Booking
                </a>
              )}
              {!token ? (
                <Link
                  to="/login"
                  className={actionButtonClass}
                >
                  Login
                </Link>
              ) : null}
              {!token ? (
                <a
                  href={registerHref}
                  className={primaryButtonClass}
                >
                  Register
                </a>
              ) : null}
              {token ? (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsAccountMenuOpen((prev) => !prev)}
                    className="flex min-h-11 max-w-[320px] items-center gap-2 rounded-xl border border-[#44529b] bg-[#1f2757] px-2 py-1 text-left text-slate-100"
                    title={salonContextLabelWithUser}
                    aria-expanded={isAccountMenuOpen}
                    aria-haspopup="menu"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/50 bg-slate-100 text-[10px] font-semibold text-slate-700">
                      {userAvatarUrl ? (
                        <img
                          src={userAvatarUrl}
                          alt={userDisplayName || "User"}
                          className="h-full w-full object-cover"
                          loading="eager"
                          onError={handleAvatarError}
                        />
                      ) : (
                        <span aria-hidden="true">{userInitials}</span>
                      )}
                    </span>
                    <span className="min-w-0 flex-1 leading-tight">
                      <span className="block truncate text-xs font-semibold text-white">
                        {userDisplayName || "Signed in user"}
                      </span>
                      <span className="block whitespace-normal break-words text-[10px] leading-tight text-slate-300">
                        {currentSalonLabel}
                      </span>
                    </span>
                  </button>
                  {isAccountMenuOpen ? (
                    <div className="absolute right-0 top-full z-[2700] mt-2 w-[min(92vw,26rem)] rounded-2xl border border-slate-200 bg-white p-3 text-slate-900 shadow-2xl">
                      <div className="border-b border-slate-200 px-3 pb-3">
                        <div className="text-sm font-semibold text-slate-900">
                          {userDisplayName || "Signed in user"}
                        </div>
                        <div className="mt-1 whitespace-normal break-words text-xs leading-5 text-slate-500">
                          {currentSalonLabel}
                        </div>
                      </div>
                      {hasSalonSwitcher ? (
                        <label className="mt-3 block min-w-0 px-3">
                          <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Switch Salon
                          </span>
                          <div className="mt-2 block min-w-0 max-w-full">
                            <select
                              aria-label="Switch Salon"
                              title={currentSalonLabel}
                              value={Number.isFinite(currentSalonId) ? String(currentSalonId) : ""}
                              onChange={handleSalonChange}
                              disabled={isSwitchingSalon}
                              className="block box-border min-h-11 w-full max-w-full min-w-0 truncate rounded-lg border border-slate-300 px-3 py-2 pr-10 text-[13px] font-semibold leading-5 text-slate-900"
                            >
                              {accessibleSalonList.map((entry) => {
                                const entryId = Number(
                                  entry?.salon_id ?? entry?.id ?? entry?.salonId ?? null
                                );
                                return (
                                  <option
                                    key={entryId}
                                    value={String(entryId)}
                                    style={SALON_SWITCH_OPTION_STYLE}
                                  >
                                    {isIndependentProfessional
                                      ? formatIndependentProfessionalContextLabel(user, entry)
                                      : formatNavSalonLabel(entry)}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        </label>
                      ) : (
                        <div className="mt-3 px-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Switch Salon
                        </div>
                      )}
                      <div className="mt-3 space-y-1">
                        <Link to={profilePath} className={accountMenuItemClass}>
                          My Profile
                        </Link>
                        <a
                          href={opsLaunchHref}
                          onClick={handleOpsLaunch}
                          className={accountMenuServiceLinkClass}
                        >
                          Open Ellyanna Booking
                        </a>
                        {isAccountingHost ? (
                          <a
                            href={marketplaceSwitchHref}
                            onClick={handleMarketplaceLaunch}
                            className={accountMenuServiceLinkClass}
                          >
                            Open MyBoothShare
                          </a>
                        ) : (
                          <a
                            href={accountingSwitchHref}
                            onClick={handleSimpleProductLaunch}
                            className={accountMenuServiceLinkClass}
                          >
                            Open SalonBusinessCPA
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={handleLogout}
                          className={accountMenuItemClass}
                        >
                          Logout
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </nav>
          <button
            type="button"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-slate-800 text-white shadow-sm transition hover:bg-slate-700 md:hidden"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          >
            <MobileMenuIcon open={isMobileMenuOpen} />
          </button>
        </div>

        {showSalonContext ? (
          <div className="mt-3 border-t border-white/10 pt-3 md:hidden">
            <div
              className="flex min-h-10 items-center justify-start gap-2 rounded-sm border border-[#44529b] bg-[#1f2757] px-2 py-2 text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
              title={salonContextLabelWithUser}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/50 bg-slate-100 text-[11px] font-semibold text-slate-700">
                {userAvatarUrl ? (
                  <img
                    src={userAvatarUrl}
                    alt={userDisplayName || "Staff"}
                    className="h-full w-full object-cover"
                    loading="eager"
                    onError={handleAvatarError}
                  />
                ) : (
                  <span aria-hidden="true">{userInitials}</span>
                )}
              </span>
              <div className="min-w-0 flex-1 leading-tight">
                <div className="truncate text-[13px] font-semibold text-white">
                  {userDisplayName || "Signed in user"}
                </div>
                {hasSalonSwitcher ? (
                  <>
                    <div className="truncate text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-300">
                      Switch Salon
                    </div>
                    <select
                      aria-label="Salon context mobile"
                      value={Number.isFinite(currentSalonId) ? String(currentSalonId) : ""}
                      onChange={handleSalonChange}
                      disabled={isSwitchingSalon}
                      className="mt-1 w-full min-w-0 rounded-md border border-white/10 bg-white/10 px-2 py-1 text-left text-[12px] font-semibold text-white outline-none transition disabled:cursor-wait disabled:opacity-70"
                    >
                      {accessibleSalonList.map((entry) => {
                        const entryId = Number(
                          entry?.salon_id ?? entry?.id ?? entry?.salonId ?? null
                        );
                        return (
                          <option
                            key={entryId}
                            value={String(entryId)}
                            style={SALON_SWITCH_OPTION_STYLE}
                          >
                            {isIndependentProfessional
                              ? formatIndependentProfessionalContextLabel(user, entry)
                              : formatNavSalonLabel(entry)}
                          </option>
                        );
                      })}
                    </select>
                  </>
                ) : (
                  <div className="truncate text-[12px] italic text-slate-200/90">
                    {currentSalonLabel}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {salonSwitchError ? (
          <div className="mt-3 rounded-2xl border border-rose-300/40 bg-rose-400/10 px-4 py-3 text-sm text-rose-100">
            {salonSwitchError}
          </div>
        ) : null}

        {isMobileMenuOpen ? (
          <div className="mt-3 rounded-2xl border border-white/10 bg-slate-900/95 p-3 shadow-2xl md:hidden">
            <div className="space-y-2">
              {token && !hasDirectOpsAccess ? (
                <Link
                  to="/account/ellyanna-access"
                  className={mobileLinkClass}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span>Get Ellyanna Access</span>
                </Link>
              ) : (
                <a
                  href={opsLaunchHref}
                  className={mobileLinkClass}
                  onClick={handleOpsLaunch}
                >
                  <span>Open Ellyanna Booking</span>
                </a>
              )}
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={mobileLinkClass}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Icon />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
              {showPublicMarketplaceLandingLinks
                ? publicMarketplaceLandingLinks.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      className={mobileLinkClass}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span>{item.label}</span>
                    </a>
                  ))
                : null}
              {showPublicAccountingLandingLinks
                ? publicAccountingLandingLinks.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      className={mobileLinkClass}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span>{item.label}</span>
                    </a>
                  ))
                : null}
              {token && showSuperAdminLink && !isAccountingHost ? (
                <NavLink
                  to="/superadmin"
                  className={mobileLinkClass}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <SuperAdminNavIcon />
                  <span>Super Admin</span>
                </NavLink>
              ) : null}
            </div>

            {!token ? (
              <div className="mt-3 grid grid-cols-2 gap-2 border-t border-white/10 pt-3">
                <Link
                  to="/login"
                  className={`${actionButtonClass} justify-center`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <a
                  href={registerHref}
                  className={`${primaryButtonClass} justify-center`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Register
                </a>
              </div>
            ) : (
              <div className="mt-3 space-y-2 border-t border-white/10 pt-3">
                <Link
                  to={profilePath}
                  className={mobileLinkClass}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <ProfileNavIcon />
                  <span>My Profile</span>
                </Link>
                <a
                  href={opsLaunchHref}
                  onClick={handleOpsLaunch}
                  className={mobileAccountMenuServiceLinkClass}
                >
                  Open Ellyanna Booking
                </a>
                {isAccountingHost ? (
                  <a
                    href={marketplaceSwitchHref}
                    onClick={handleMarketplaceLaunch}
                    className={mobileAccountMenuServiceLinkClass}
                  >
                    Open MyBoothShare
                  </a>
                ) : (
                  <a
                    href={accountingSwitchHref}
                    onClick={handleSimpleProductLaunch}
                    className={mobileAccountMenuServiceLinkClass}
                  >
                    Open SalonBusinessCPA
                  </a>
                )}
                <button
                  type="button"
                  onClick={handleLogout}
                  className={mobileLinkClass}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </header>
  );
}
